import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { X, TrendingUp } from 'lucide-react';
import { Inversion } from '../types';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

interface Props {
  inversion: Inversion;
  onClose: () => void;
}

export const ModalActualizarValor: React.FC<Props> = ({ inversion, onClose }) => {
  useCerrarConEscape(onClose);
  const { actualizarValorInversion } = useData();
  const [valor, setValor]   = useState(inversion.valorActual.toString());
  const [fecha, setFecha]   = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const valorNum   = aNumero(valor) || 0;
  const diferencia = valorNum - inversion.valorActual;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (valorNum < 0) { setError('El valor no puede ser negativo'); return; }
    setLoading(true);
    try {
      await actualizarValorInversion(inversion.id, valorNum, new Date(fecha));
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center capa-hoja z-50">
      <div className="hoja bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-sm max-h-full overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Actualizar valor</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {inversion.nombre}
            </p>
            <p className="text-xs text-gray-400">
              Valor actual: <span className="font-semibold">€{inversion.valorActual.toFixed(2)}</span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Nuevo valor (€)
            </label>
            <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE} title={AVISO_IMPORTE} value={valor}
              onChange={e => setValor(e.target.value)}
              className={`${inputCls} text-lg font-bold`} autoFocus required />
            {Math.abs(diferencia) > 0.01 && (
              <p className={`text-xs mt-1 font-semibold ${diferencia > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {diferencia > 0 ? '+' : ''}€{diferencia.toFixed(2)} respecto al valor anterior
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Fecha de la valoración
            </label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
              className={inputCls} required />
          </div>

          <div className="flex gap-3 pb-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition disabled:opacity-50">
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
